import "./globals";
import "./registerServiceWorker";
import "./main.css";

// Force MetaMask dark mode globally — no light mode support
document.documentElement.classList.add("dark");

import { createGtm, type GtmSupport } from "@gtm-support/vue-gtm";
import { createIcons } from "@toruslabs/vue-components/icons";
import { createApp } from "vue";
import VueDOMPurifyHTML from "vue-dompurify-html";

import App from "./App.vue";
import config from "./config";
import auth0Handler, { getAuth0Client } from "./helpers/auth/auth0Handler";
import { applyStoredConsent, initConsent, registerGtm } from "./helpers/consent";
import { initUserConsentPersistence } from "./helpers/userConsentPersistence";
import Icons from "./icons";
import i18n, { loadLanguageAsync } from "./plugins/i18n-setup";
import router from "./router";
import { installSentry } from "./utils/sentry";
import { getUserLanguage } from "./utils/utils";
import store from "./vuexStore";

initUserConsentPersistence();
initConsent();

const app = createApp(App);

// Warm up Auth0 during bootstrap (guarded when env vars are absent):
// - Social: createAuth0Client() runs a silent checkSession() so session restoration overlaps with mount.
// - Passwordless: schedule proactive token renewal and re-check on tab focus (auth0-js has no refresh tokens).
if (config.auth0Domain && config.auth0ClientId) {
  void getAuth0Client();
  auth0Handler.initPasswordlessAutoRenew();
}

app
  .use(VueDOMPurifyHTML)
  .use(i18n)
  .use(
    createGtm({
      id: "GTM-5WPZZ4QC",
      defer: true,
      enabled: true,
      debug: false,
      loadScript: true,
      vueRouter: router,
      trackOnNextTick: true,
    })
  )
  .use(router)
  .use(store)
  .use(createIcons(Icons));

registerGtm(app.config.globalProperties.$gtm as GtmSupport);

app.mount("#app");

applyStoredConsent();

installSentry();

loadLanguageAsync(getUserLanguage());
